/**
 * Domain analytics rows for Reports › Domain Analytics.
 * Pre-computed rollup — how many hours, events, unique employees, and the
 * billable / non-billable split each tracked domain accounted for across
 * the selected period, plus the top application seen on that domain.
 *
 * Extended in task 115 with `appName`, `otherAppCount`, `billableHours`,
 * `nonBillableHours`, `avgHoursPerUser` (dropping the weak `percentOfTotal`
 * signal). Internally-consistent invariants — see the Zod schema in
 * `src/data/schemas/domains.ts` for the machine-checked rules; values
 * satisfy `billableHours + nonBillableHours <= totalHours` (the delta is
 * unclassified time) and `avgHoursPerUser ≈ round(totalHours / employees, 1)`.
 */

export interface DomainRow {
  domain: string;
  /** Top app by event count for the domain in the window. */
  appName: string;
  /** Other distinct apps beyond the top one (0 when only one app). */
  otherAppCount: number;
  employees: number;
  events: number;
  totalHours: number;
  billableHours: number;
  nonBillableHours: number;
  /** `totalHours / employees`, rounded to 1 dp. */
  avgHoursPerUser: number;
}

export const DOMAINS = [
  {
    domain: 'outlook.office.com',
    appName: 'Outlook',
    otherAppCount: 2,
    employees: 8,
    events: 1710,
    totalHours: 142.5,
    billableHours: 98.2,
    nonBillableHours: 41.4,
    avgHoursPerUser: 17.8,
  },
  {
    domain: 'westlaw.com',
    appName: 'Chrome',
    otherAppCount: 1,
    employees: 6,
    events: 1180,
    totalHours: 98.3,
    billableHours: 92.1,
    nonBillableHours: 4.7,
    avgHoursPerUser: 16.4,
  },
  {
    domain: 'docs.google.com',
    appName: 'Chrome',
    otherAppCount: 2,
    employees: 7,
    events: 1046,
    totalHours: 87.2,
    billableHours: 60.3,
    nonBillableHours: 24.6,
    avgHoursPerUser: 12.5,
  },
  {
    domain: 'lexisnexis.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 5,
    events: 865,
    totalHours: 72.1,
    billableHours: 68.4,
    nonBillableHours: 3.1,
    avgHoursPerUser: 14.4,
  },
  {
    domain: 'teams.microsoft.com',
    appName: 'Teams',
    otherAppCount: 1,
    employees: 8,
    events: 790,
    totalHours: 65.8,
    billableHours: 31.2,
    nonBillableHours: 33.1,
    avgHoursPerUser: 8.2,
  },
  {
    domain: 'sec.gov',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 3,
    events: 542,
    totalHours: 45.2,
    billableHours: 44.8,
    nonBillableHours: 0.3,
    avgHoursPerUser: 15.1,
  },
  {
    domain: 'salesforce.com',
    appName: 'Chrome',
    otherAppCount: 1,
    employees: 4,
    events: 499,
    totalHours: 41.6,
    billableHours: 28.4,
    nonBillableHours: 12.8,
    avgHoursPerUser: 10.4,
  },
  {
    domain: 'bloomberg.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 2,
    events: 464,
    totalHours: 38.7,
    billableHours: 30.2,
    nonBillableHours: 8.1,
    avgHoursPerUser: 19.4,
  },
  {
    domain: 'sharepoint.com',
    appName: 'Edge',
    otherAppCount: 1,
    employees: 7,
    events: 426,
    totalHours: 35.5,
    billableHours: 22.1,
    nonBillableHours: 13.0,
    avgHoursPerUser: 5.1,
  },
  {
    domain: 'slack.com',
    appName: 'Slack',
    otherAppCount: 1,
    employees: 6,
    events: 385,
    totalHours: 32.1,
    billableHours: 10.2,
    nonBillableHours: 21.6,
    avgHoursPerUser: 5.4,
  },
  {
    domain: 'clio.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 5,
    events: 358,
    totalHours: 29.8,
    billableHours: 28.4,
    nonBillableHours: 1.2,
    avgHoursPerUser: 6.0,
  },
  {
    domain: 'onedrive.live.com',
    appName: 'OneDrive',
    otherAppCount: 1,
    employees: 5,
    events: 341,
    totalHours: 28.4,
    billableHours: 18.2,
    nonBillableHours: 9.8,
    avgHoursPerUser: 5.7,
  },
  {
    domain: 'zoom.us',
    appName: 'Zoom',
    otherAppCount: 1,
    employees: 4,
    events: 298,
    totalHours: 24.8,
    billableHours: 16.4,
    nonBillableHours: 8.1,
    avgHoursPerUser: 6.2,
  },
  {
    domain: 'docusign.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 3,
    events: 268,
    totalHours: 22.3,
    billableHours: 21.8,
    nonBillableHours: 0.4,
    avgHoursPerUser: 7.4,
  },
  {
    domain: 'law.cornell.edu',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 4,
    events: 222,
    totalHours: 18.5,
    billableHours: 18.2,
    nonBillableHours: 0.2,
    avgHoursPerUser: 4.6,
  },
  {
    domain: 'courtlistener.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 2,
    events: 182,
    totalHours: 15.2,
    billableHours: 14.8,
    nonBillableHours: 0.3,
    avgHoursPerUser: 7.6,
  },
  {
    domain: 'pacer.gov',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 2,
    events: 154,
    totalHours: 12.8,
    billableHours: 12.5,
    nonBillableHours: 0.2,
    avgHoursPerUser: 6.4,
  },
  {
    domain: 'github.com',
    appName: 'Chrome',
    otherAppCount: 1,
    employees: 1,
    events: 126,
    totalHours: 10.5,
    billableHours: 8.1,
    nonBillableHours: 2.2,
    avgHoursPerUser: 10.5,
  },
  {
    domain: 'servicenow.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 1,
    events: 98,
    totalHours: 8.2,
    billableHours: 6.4,
    nonBillableHours: 1.8,
    avgHoursPerUser: 8.2,
  },
  {
    domain: 'linkedin.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 3,
    events: 78,
    totalHours: 6.5,
    billableHours: 1.2,
    nonBillableHours: 5.1,
    avgHoursPerUser: 2.2,
  },
  {
    domain: 'fda.gov',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 2,
    events: 70,
    totalHours: 5.8,
    billableHours: 5.6,
    nonBillableHours: 0.2,
    avgHoursPerUser: 2.9,
  },
  {
    domain: 'taxjar.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 1,
    events: 61,
    totalHours: 5.1,
    billableHours: 5.0,
    nonBillableHours: 0.1,
    avgHoursPerUser: 5.1,
  },
  {
    domain: 'figma.com',
    appName: 'Figma',
    otherAppCount: 1,
    employees: 1,
    events: 50,
    totalHours: 4.2,
    billableHours: 2.1,
    nonBillableHours: 2.0,
    avgHoursPerUser: 4.2,
  },
  {
    domain: 'notion.so',
    appName: 'Chrome',
    otherAppCount: 1,
    employees: 2,
    events: 37,
    totalHours: 3.1,
    billableHours: 1.4,
    nonBillableHours: 1.6,
    avgHoursPerUser: 1.6,
  },
  {
    domain: 'meridian-associates.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 8,
    events: 35,
    totalHours: 2.9,
    billableHours: 0.6,
    nonBillableHours: 2.2,
    avgHoursPerUser: 0.4,
  },
  {
    domain: 'uspto.gov',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 2,
    events: 33,
    totalHours: 2.7,
    billableHours: 2.6,
    nonBillableHours: 0.1,
    avgHoursPerUser: 1.4,
  },
  {
    domain: 'ecfr.gov',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 1,
    events: 22,
    totalHours: 1.8,
    billableHours: 1.6,
    nonBillableHours: 0.2,
    avgHoursPerUser: 1.8,
  },
  {
    domain: 'justia.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 1,
    events: 14,
    totalHours: 1.2,
    billableHours: 1.1,
    nonBillableHours: 0.1,
    avgHoursPerUser: 1.2,
  },
  {
    domain: 'findlaw.com',
    appName: 'Chrome',
    otherAppCount: 0,
    employees: 1,
    events: 11,
    totalHours: 0.9,
    billableHours: 0.8,
    nonBillableHours: 0.1,
    avgHoursPerUser: 0.9,
  },
  {
    domain: 'chat.openai.com',
    appName: 'Chrome',
    otherAppCount: 1,
    employees: 3,
    events: 10,
    totalHours: 0.8,
    billableHours: 0.1,
    nonBillableHours: 0.6,
    avgHoursPerUser: 0.3,
  },
] as const satisfies readonly DomainRow[];
